import AppIcon from "common/app-icon";
import flatten from "lodash/flatten";
import * as React from "react";
import { Caption, LeftContainer, Value } from "../expandable-options/expandable-options-style";
import { RatingContainer, RightContainer } from "./rating-style";

interface Props {
    readonly ratingValue: number;
    readonly caption: string;
    readonly onChange?: (newVal: number) => void;
    readonly maxRating: number;
    readonly ratingUnit: string;
}
interface State {
    selectedValue: number | undefined;
    isMouseDown: boolean;
}

export default class RatingComponent extends React.PureComponent<Props, State> {
    public state: State = {
        selectedValue: this.props.ratingValue,
        isMouseDown: false,
    };

    private ratingRef = React.createRef<HTMLInputElement>();

    public static getDerivedStateFromProps(props: Props, state: State) {
        if (props.ratingValue !== state.selectedValue && !state.isMouseDown) {
            state.selectedValue = props.ratingValue;
        }

        return state;
    }

    public componentDidMount() {
        this.ratingRef.current?.addEventListener("touchmove", this.onTouchMove, {
            capture: true,
            passive: false,
        });
    }

    public componentWillUnmount() {
        this.ratingRef.current?.removeEventListener("touchmove", this.onTouchMove, {
            capture: true,
        });
    }

    private onTouchMove = (event: TouchEvent) => {
        event.stopPropagation();
        if (event.cancelable) event.preventDefault();

        const els = document.elementsFromPoint(event.touches[0].pageX, event.touches[0].pageY);

        const selectedValue = this.getSelected(els);

        this.setState({
            selectedValue,
            isMouseDown: true,
        });
    };

    private onMouseMove = (event: React.MouseEvent) => {
        const { isMouseDown } = this.state;
        if (isMouseDown) {
            const els = document.elementsFromPoint(event.clientX, event.clientY);
            const selectedValue = this.getSelected(els);

            this.setState({
                selectedValue,
            });
        }
    };

    private onMouseDown = (event: React.MouseEvent) => {
        const els = document.elementsFromPoint(event.clientX, event.clientY);
        const selectedValue = this.getSelected(els);

        this.setState({
            selectedValue,
            isMouseDown: true,
        });
    };

    private onMouseUp = (_event: React.MouseEvent) => {
        const { selectedValue } = this.state;

        if (selectedValue !== undefined) {
            this.props.onChange?.(selectedValue);
        } else {
            this.props.onChange?.(0);
        }
        this.setState({ isMouseDown: false });
    };

    private getSelected = (els: Element[]): number => {
        const classes = flatten(els.map(e => e.classList.toString().split(" ")));
        const selectedClass = classes.find(c => c.startsWith("rating-id-"));
        if (selectedClass === undefined) return 0;
        const selected = Number.parseInt(selectedClass.split("-")[2]);
        if (typeof selected !== "number") return 0;

        return selected;
    };

    public render(): React.ReactNode {
        const { ratingValue, caption, maxRating, ratingUnit } = this.props;
        const { selectedValue } = this.state;

        let value: string;
        let icon: string;
        let size: number = 24;

        const definedVal = selectedValue === undefined ? ratingValue : selectedValue;

        if (definedVal <= 0) {
            value = "—";
        } else if (definedVal > maxRating) {
            value = `${maxRating} / ${maxRating}`;
        } else {
            value = `${definedVal} / ${maxRating}`;
        }

        switch (ratingUnit) {
            case "stars": {
                icon = "22-06-rating-star-1";
                size = 40;
                break;
            }
            case "hearts": {
                icon = "22-05-love-it";
                break;
            }
            case "money": {
                icon = "09-05-discount-dollar-dash";
                break;
            }
            case "time": {
                icon = "01-18-time-clock-circle";
                break;
            }
            case "pizzas": {
                icon = "26-04-fast-food-pizza";
                break;
            }
            default:
                icon = "22-06-rating-star-1";
                break;
        }

        return (
            <RatingContainer data-test="app-rating-component">
                <LeftContainer>
                    <Caption>{caption}</Caption>
                    <Value>{value}</Value>
                </LeftContainer>

                <RightContainer
                    className="rating-id-0"
                    ref={this.ratingRef}
                    enabled={this.props.onChange !== undefined}
                    onMouseMove={this.onMouseMove}
                    onMouseDown={this.onMouseDown}
                    onMouseUp={this.onMouseUp}>
                    {Array.from({ length: maxRating }, (_, k) => (
                        <div className={`rating-icon rating-id-${k + 1}`}>
                            <AppIcon
                                key={k}
                                icon={icon}
                                size={size}
                                color={selectedValue !== undefined && k + 1 <= selectedValue ? "#3B77E1" : "#C4C4C4"}
                            />
                        </div>
                    ))}
                </RightContainer>
            </RatingContainer>
        );
    }
}
